import {
  BottomSheetBackdrop,
  type BottomSheetBackgroundProps,
  BottomSheetModal,
  BottomSheetModalProvider,
  BottomSheetView,
  useBottomSheetTimingConfigs,
} from '@gorhom/bottom-sheet'
import {
  type ComponentProps,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
} from 'react'
import { Pressable, StyleSheet, View, type ViewStyle } from 'react-native'
import { Easing } from 'react-native-reanimated'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import {
  useBackdropBlur,
  useLiquidGlass,
} from '../providers/NativeCapabilitiesContext'
import { isDarkColor } from '../theme/isDarkColor'
import { useTheme } from '../theme/restyle'
import { withAlpha } from '../utils/withAlpha'
import type { useLockFlowDismiss } from './FlowDismissLock'
import { sheetSizing, useInertScrollableFocus } from './sheetSizing'
import { useKeyboardHeight } from './useKeyboardHeight'
import { useV2DrawerMotion } from './useV2DrawerMotion'

/**
 * Material laid under a translucent flow surface. The package ships its own
 * (see SheetMaterial); a host replaces it through `FunkitProvider`. Rendered
 * into a full-bleed container, so `StyleSheet.absoluteFill` fills the screen.
 * The material carries its own tint; a glass one (see `liquidGlass`) has the
 * surface paint no fill over it, which would mute the refraction to a panel.
 */
export type BackdropBlurRenderer = () => ReactNode

export interface FlowSheetProps {
  isOpen: boolean
  /**
   * Per-`beginDeposit` id. A change while open re-presents the sheet, which
   * cancels an in-flight dismiss — so opening a new session mid-swipe-dismiss
   * isn't closed by the previous session's late `onDismiss`.
   */
  session: number
  onClose: () => void
  /** The flow screens rendered inside the sheet. */
  content: ReactNode
  /** The host app subtree the sheet is presented above. */
  children: ReactNode
  /**
   * Lift the sheet above the keyboard. Scoped to the screen that needs it (the
   * Swapped embed's WebView): a generic flow screen with no inputs shouldn't
   * subscribe to keyboard events, and a future `BottomSheetTextInput` screen
   * would be handled by gorhom natively instead. See {@link useKeyboardHeight}.
   */
  keyboardAvoidEnabled?: boolean
  enableSwipeToDismiss?: boolean
  /**
   * Hold the sheet still: no swipe-to-dismiss, no rubber-band drag, no
   * backdrop tap. For a step whose own screens the user is part-way through
   * (see {@link useLockFlowDismiss}); their back controls are the way off.
   */
  dismissLocked?: boolean
  /** Let the sheet reach the top of the screen for a screen that is a full window tall. */
  fullHeight?: boolean
  /**
   * Draw the sheet as bare material rather than a panel — the V2 deposit
   * surface, which the design lays over the host app. See
   * {@link GlassSheetBackground}.
   */
  glass?: boolean
}

/** The subset of `BottomSheetModal`'s imperative handle the flow drives. */
interface SheetHandle {
  present: () => void
  dismiss: () => void
}

/**
 * Host for the managed deposit-flow surface: a `@gorhom/bottom-sheet` modal
 * (native gestures + spring physics), sized to its content. The flow screens
 * draw their own grabber, so the sheet runs handle-less; its surface is painted
 * by {@link FlowSheetBackground} (which also fills the over-drag gap).
 *
 * Requires the gorhom peer stack (`@gorhom/bottom-sheet`,
 * `react-native-gesture-handler`, `react-native-reanimated`) and a
 * `<GestureHandlerRootView>` at the app root.
 */
// A pan has to travel this far before it counts, so a locked sheet never sees
// one: the finger would leave the screen first.
const LOCKED_PAN_ACTIVATION: [number, number] = [-100000, 100000]

export function FlowSheet({
  isOpen,
  session,
  onClose,
  content,
  children,
  keyboardAvoidEnabled = false,
  enableSwipeToDismiss = true,
  dismissLocked = false,
  fullHeight = false,
  glass = false,
}: FlowSheetProps) {
  const ref = useRef<SheetHandle | null>(null)
  const setRef = useCallback((instance: SheetHandle | null) => {
    ref.current = instance
  }, [])

  const keyboardHeight = useKeyboardHeight(keyboardAvoidEnabled)
  const insets = useSafeAreaInsets()
  const theme = useTheme()
  const { t } = useFunkitTranslation()

  // Bridge the flow's `isOpen` state onto the modal's imperative present/dismiss.
  // Two guards avoid gorhom's "dismiss on an already-closed modal → unmount +
  // mark DISMISSED (can't re-present)" footgun:
  //   - `hasPresented`: never dismiss a modal we never presented (initial mount).
  //   - `sheetSelfDismissed`: when a swipe/backdrop closes the sheet, gorhom
  //     already dismissed it and fired onDismiss → onClose → isOpen=false; skip
  //     the redundant dismiss so it can reopen.
  const hasPresented = useRef(false)
  const sheetSelfDismissed = useRef(false)
  useEffect(() => {
    if (isOpen) {
      sheetSelfDismissed.current = false
      ref.current?.present()
      hasPresented.current = true
      return
    }
    if (!hasPresented.current) {
      return
    }
    hasPresented.current = false
    if (sheetSelfDismissed.current) {
      sheetSelfDismissed.current = false
      return
    }
    ref.current?.dismiss()
    // `session` is a dep so a new beginDeposit while open re-presents (and
    // cancels any in-flight dismiss) rather than leaving a stale close pending.
  }, [isOpen, session])

  const handleDismiss = useCallback(() => {
    sheetSelfDismissed.current = true
    onClose()
  }, [onClose])

  const dismissLockedRef = useRef(dismissLocked)
  dismissLockedRef.current = dismissLocked
  const closeUnlessLocked = useCallback(() => {
    if (!dismissLockedRef.current) {
      ref.current?.dismiss()
    }
  }, [])

  // Smooth the dynamic-size morph: when the active flow screen changes height,
  // gorhom recomputes the dynamic snap point and animates to it with these
  // configs (this also governs present/dismiss). Ease-out cubic-bezier
  // (0.22, 1, 0.36, 1) over 300ms matches the team's container-resize motion
  // token, so the sheet tweens between screen heights instead of snapping.
  const animationConfigs = useBottomSheetTimingConfigs({
    duration: 300,
    easing: Easing.bezier(0.22, 1, 0.36, 1),
  })
  const v2AnimationConfigs = useV2DrawerMotion()

  // A panel sheet lays the material under its overlayFill. A glass sheet
  // carries it on its own surface instead (see GlassSheetBackground): the
  // design blurs only what that sheet covers.
  const renderBackdropBlur = useBackdropBlur()
  const blurBehindOverlay = glass ? null : renderBackdropBlur

  // Every layer hangs off gorhom's backdrop so its fade and tap-to-close own
  // them, with the overlay over the blur (the design's `backdrop-filter` order).
  const renderBackdrop = useCallback(
    (props: ComponentProps<typeof BottomSheetBackdrop>) => (
      <BottomSheetBackdrop
        {...props}
        appearsOnIndex={0}
        disappearsOnIndex={-1}
        // Tap-to-close is ours, read through a ref: a `pressBehavior` that
        // followed the lock would remake this callback, and gorhom mounts a
        // new backdrop for every new component it is handed.
        pressBehavior="none"
        accessible={false}
        // gorhom labels its backdrop as a button. react-native-web honours that
        // role, so the backdrop becomes a <button> wrapping our close target —
        // a button inside a button. The close affordance is the Pressable
        // below, which keeps the role.
        accessibilityRole="none"
        // gorhom's backdrop paints opaque black scaled by `opacity`; the alpha
        // rides our overlay color instead, so the container clears to transparent
        // and `opacity` only drives the fade.
        opacity={1}
        style={backdropContainer}
      >
        {blurBehindOverlay ? (
          <View style={StyleSheet.absoluteFill} pointerEvents="none">
            {blurBehindOverlay()}
          </View>
        ) : null}
        <View
          style={[
            overlayFill,
            {
              backgroundColor: glass ? GLASS_OVERLAY : theme.background.overlay,
            },
          ]}
          pointerEvents="none"
        />
        <Pressable
          style={StyleSheet.absoluteFill}
          accessibilityRole="button"
          accessibilityLabel={t('common.close')}
          onPress={closeUnlessLocked}
        />
      </BottomSheetBackdrop>
    ),
    [blurBehindOverlay, closeUnlessLocked, glass, theme.background.overlay, t],
  )

  return (
    <BottomSheetModalProvider>
      {children}
      <BottomSheetModal
        ref={setRef}
        // We lift the sheet ourselves by growing the content (the spacer below,
        // driven by `keyboardAvoidEnabled`). gorhom doesn't react to the
        // WebView's keyboard, so `extend` just keeps it at its (grown) content
        // height — our spacer is the single source of truth.
        keyboardBehavior="extend"
        // Full-height screens have no room to rubber-band. Keyboard compensation
        // must also stay anchored so dragging cannot expose its blank spacer.
        enableOverDrag={!fullHeight && keyboardHeight === 0 && !dismissLocked}
        // A locked step is a screen, not a drawer: nothing about it drags. Done
        // by never letting the pan activate rather than through
        // `enableContentPanningGesture`, which gorhom answers by swapping the
        // content's wrapper component — remounting every screen inside it.
        activeOffsetY={dismissLocked ? LOCKED_PAN_ACTIVATION : undefined}
        // Full-height steps scroll with gorhom's scrollable, which can't be
        // measured (see sheetSizing); the rest are sized to their content.
        {...sheetSizing({ fullHeight })}
        // Cap the sheet at the bottom of the status bar: gorhom subtracts this
        // from the container height, so a tall screen tops out flush below it
        // instead of sliding behind it. A full-height screen waives the cap and
        // pays the inset itself, or the overlay shows as a strip above it.
        topInset={fullHeight ? 0 : insets.top}
        // Off on non-home screens: the drag rubber-bands back instead of
        // closing (gorhom keeps the pan gesture, just not the dismissal).
        enablePanDownToClose={enableSwipeToDismiss && !dismissLocked}
        animationConfigs={glass ? v2AnimationConfigs : animationConfigs}
        onDismiss={handleDismiss}
        backdropComponent={renderBackdrop}
        // The flow screens render their own grabber; the sheet paints the
        // surface (see FlowSheetBackground) so over-drag doesn't expose a gap.
        handleComponent={null}
        backgroundComponent={glass ? GlassSheetBackground : FlowSheetBackground}
      >
        {/* Wrapped either way — swapping the element on a `fullHeight` flip
            would remount the flow and lose its state. */}
        <BottomSheetView
          focusHook={fullHeight ? useInertScrollableFocus : undefined}
        >
          {content}
          {/* Lift the sheet above the keyboard. The Swapped embed focuses an
              input inside its WebView (see useKeyboardHeight) which gorhom's
              keyboard handling can't see, so the sheet won't move on its own.
              Growing the dynamically-sized content by the keyboard height
              slides the whole sheet up instead of letting the keyboard overlap
              it (which reflows the WebView's viewport smaller — the "shrink").
              gorhom tweens the resize with `animationConfigs`. */}
          {keyboardHeight > 0 ? (
            <View style={{ height: keyboardHeight }} />
          ) : null}
        </BottomSheetView>
      </BottomSheetModal>
    </BottomSheetModalProvider>
  )
}

/**
 * Sheet surface for the flow modal. gorhom sizes the sheet body to its content,
 * so a flat background fills only the content area; over-dragging the sheet
 * above its content height lifts the body off the bottom and would otherwise
 * expose the backdrop below it (ENG-4340: "you can see how the drawer ends").
 *
 * We paint the `bgModal` surface (matching each screen's own surface + rounded
 * top) and hang a tall extension off the content's bottom edge. Anchored at
 * `top: '100%'`, the extension tracks the body as it lifts, so the rubber-band
 * over-drag reveals more surface instead of empty space — the bounce is kept.
 */
export function FlowSheetBackground(props: BottomSheetBackgroundProps) {
  return <SheetSurface {...props} />
}

// The V2 deposit surface is bare material rather than a panel: the host app
// reads through it, while the material's own dark tint keeps the flow content
// from competing with whatever is behind it. Without glass the surface paints
// the fill itself.
const GLASS_SURFACE_OPACITY = 0.8
// Black in its own right, not the modal token dimmed; light themes keep theirs.
const GLASS_DARK_MATERIAL = '#000000'
// No dim: gorhom's backdrop spans the screen, so one would sit under the sheet
// and darken the blur's own input.
const GLASS_OVERLAY = 'transparent'

/**
 * {@link FlowSheetBackground} for the amount-first deposit surface: only the
 * material, with no fill, shadow, or stroke of the sheet's own. Its corners
 * are the material's, concentric with the device's.
 */
export function GlassSheetBackground({
  pointerEvents,
  style,
}: BottomSheetBackgroundProps) {
  const theme = useTheme()
  const renderMaterial = useBackdropBlur()
  const glass = useLiquidGlass()
  const color = isDarkColor(theme.background.modal)
    ? GLASS_DARK_MATERIAL
    : theme.background.modal
  return (
    <View pointerEvents={pointerEvents} style={style}>
      {renderMaterial()}
      {glass ? null : (
        <View
          style={[
            StyleSheet.absoluteFill,
            { backgroundColor: withAlpha(color, GLASS_SURFACE_OPACITY) },
          ]}
        />
      )}
    </View>
  )
}

// iOS reads `shadowRadius` as the gaussian's deviation — half the design's 64.
const GLASS_SHADOW: ViewStyle = {
  shadowColor: '#000000',
  shadowOpacity: 1,
  shadowOffset: { width: 0, height: 0 },
  shadowRadius: 32,
  elevation: 24,
}

/**
 * {@link FlowSheetBackground} for a screen stacked over another sheet — the
 * country list (2531:8471). Glass rather than a panel, so the step it was
 * raised from still reads faintly underneath it.
 */
export function StackedSheetBackground(props: BottomSheetBackgroundProps) {
  return (
    <SheetSurface
      {...props}
      opacity={STACKED_SURFACE_OPACITY}
      topRadius={STACKED_TOP_RADIUS}
      shadow={GLASS_SHADOW}
    />
  )
}

// Figma's rgba(22,22,22,0.8) (fee drawer 4006:4707), taken down the same step
// as the sheet it stacks on so the material still reads through both.
const STACKED_SURFACE_OPACITY = 0.75
// Shallower than the sheet it stacks on (36), so the two heads stay distinct.
const STACKED_TOP_RADIUS = 32

function SheetSurface({
  pointerEvents,
  style,
  opacity = 1,
  shadow,
  topRadius,
}: BottomSheetBackgroundProps & {
  opacity?: number
  shadow?: ViewStyle
  topRadius?: number
}) {
  const theme = useTheme()
  // A translucent sheet carries the material itself rather than taking it
  // from the backdrop, because the design blurs only what the sheet covers —
  // a drawer leaves the screen above it sharp, merely dimmed.
  const renderBackdropBlur = useBackdropBlur()
  const material = opacity < 1 ? renderBackdropBlur : null
  // Figma draws these surfaces as Glass: refraction and dispersion through the
  // fill, not a blur behind it. A glass material owns the tint, and the fill
  // below drops out from under it.
  const glass = useLiquidGlass() && material != null
  // Composed rather than applied as view `opacity`, which would fade the
  // over-drag extension's own overlap and the sheet's shadow with it.
  const fill = withAlpha(theme.background.modal, opacity)
  const surfaceFill = glass ? 'transparent' : fill
  const radius = topRadius ?? theme.borderRadii.modal
  const rounded: ViewStyle = {
    borderTopLeftRadius: radius,
    borderTopRightRadius: radius,
    // The design's 60% corner smoothing: iOS's continuous curve, which every
    // sheet corner is shallow enough to take (no-op on Android).
    borderCurve: 'continuous',
  }
  const surface: ViewStyle = { ...rounded, backgroundColor: surfaceFill }
  return (
    <View pointerEvents={pointerEvents} style={style}>
      {/* Under the fill, so the blur samples the host app and not the fill. */}
      {material ? (
        <View style={[StyleSheet.absoluteFill, rounded, clipToSurface]}>
          <View style={materialBleed}>{material()}</View>
        </View>
      ) : null}
      {/* Unclipped: `overflow: hidden` would mask the shadow too. */}
      <View style={[StyleSheet.absoluteFill, surface, shadow]} />
      {/* The blur is clipped to each surface, so the extension carries its own
          — without it a rubber-band pull shows a sharp band of host content
          under the blurred sheet. */}
      {material ? (
        <View style={[bottomExtension, clipToSurface]}>
          <View style={extensionMaterialBleed}>{material()}</View>
        </View>
      ) : null}
      <View style={[bottomExtension, { backgroundColor: surfaceFill }]} />
    </View>
  )
}

// iOS 26 Glass paints a specular rim on its own bounds, and the design strokes
// neither surface (4006:4673, 4006:4707) — on a full-bleed sheet that rim read
// as a bright line down each screen edge and across the drawer's head. The
// material is laid out past the clip instead, so the rim is cut away with it.
const MATERIAL_RIM = 2

const materialBleed: ViewStyle = {
  position: 'absolute',
  top: -MATERIAL_RIM,
  left: -MATERIAL_RIM,
  right: -MATERIAL_RIM,
  bottom: -MATERIAL_RIM,
}

// The extension's head meets the surface's foot, so it keeps that edge in
// place: pulling it up would lay a second sheet of material over the seam.
const extensionMaterialBleed: ViewStyle = { ...materialBleed, top: 0 }

const clipToSurface: ViewStyle = { overflow: 'hidden' }

const backdropContainer: ViewStyle = { backgroundColor: 'transparent' }

const overlayFill: ViewStyle = StyleSheet.absoluteFill

// Comfortably taller than any phone screen so even a hard rubber-band pull never
// drags past the surface; it extends below the device edge and stays off-screen
// at rest.
const bottomExtension: ViewStyle = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  height: 1000,
}
